import React, { useEffect, useState } from 'react';
import { Download, Loader2, Plus, Printer, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { errMsg, kApi, parseYouTubeId } from '../../lib/karaokeApi';

export const TeamPanel = () => {
  const [rows, setRows] = useState(null);
  const load = () => kApi.team().then(setRows).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!rows) return <Loader2 className="w-6 h-6 animate-spin text-slate-400" />;
  const set = async (id, role) => {
    try { await kApi.setTeam(id, role || null); toast.success('Karaoke role saved'); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2" data-testid="karaoke-team">
      <p className="text-sm text-slate-600">Choose who works Karaoke Night. DJ controls playback and the projector; Host and Operator have view access (Operators can also submit requests for guests).</p>
      {rows.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
          <div className="min-w-0 flex-1"><p className="font-semibold text-slate-900 break-words">{r.name || r.email}</p><p className="text-xs text-slate-500">{r.role}</p></div>
          <select value={r.karaoke_role || ''} onChange={(e) => set(r.id, e.target.value)} className="h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid={`karaoke-team-role-${r.id}`}>
            <option value="">No karaoke role</option><option value="dj">DJ</option><option value="host">Host</option><option value="operator">Operator</option>
          </select>
        </div>
      ))}
    </div>
  );
};

export const QrPanel = ({ code }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    kApi.qrBlob().then((b) => { u = URL.createObjectURL(b); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, []);
  const link = `${window.location.origin}/karaoke/r/${code}`;
  return (
    <div className="flex flex-col sm:flex-row gap-6 items-start" data-testid="karaoke-qr">
      <div className="rounded-2xl bg-white border border-slate-200 p-4">{url ? <img src={url} alt="Karaoke QR code" className="w-56 h-56" data-testid="karaoke-qr-image" /> : <Loader2 className="w-6 h-6 animate-spin" />}</div>
      <div className="space-y-3 min-w-0">
        <p className="text-sm text-slate-700">This permanent QR code always opens your hotel's Karaoke request page — print it for the lollipop stands. Requests go to whichever session is active; when none is, guests see "Karaoke is not currently active".</p>
        <p className="text-xs text-slate-500 break-all">{link}</p>
        <div className="flex gap-2">
          {url && <a href={url} download="ic-karaoke-qr.png" className="h-10 px-4 rounded-xl bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-1.5" data-testid="karaoke-qr-download"><Download className="w-4 h-4" /> Download PNG</a>}
          {url && <button type="button" onClick={() => { const w = window.open(''); w.document.write(`<img src="${url}" style="width:90vmin" onload="window.print()">`); }} className="h-10 px-4 rounded-xl bg-slate-100 text-sm font-semibold inline-flex items-center gap-1.5"><Printer className="w-4 h-4" /> Print</button>}
        </div>
      </div>
    </div>
  );
};

// Operators / DJ submit a request on behalf of a guest (links only).
export const StaffRequestForm = ({ onDone }) => {
  const [name, setName] = useState('');
  const [room, setRoom] = useState('');
  const [songs, setSongs] = useState([{ url: '', title: '' }]);
  const [busy, setBusy] = useState(false);
  const valid = name.trim() && room.trim() && songs.every((s) => parseYouTubeId(s.url));
  const submit = async () => {
    setBusy(true);
    try {
      await kApi.staffRequest({ guest_name: name.trim(), room_number: room.trim(), songs: songs.map((s) => ({ url: s.url, title: s.title || null })), client_nonce: `${Date.now()}` });
      toast.success('Request sent to the DJ'); setName(''); setRoom(''); setSongs([{ url: '', title: '' }]); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="karaoke-staff-request">
      <p className="font-bold text-slate-900">Request for a guest</p>
      <div className="grid grid-cols-3 gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Guest name" className="col-span-2 h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid="karaoke-staff-name" />
        <input value={room} onChange={(e) => setRoom(e.target.value)} placeholder="Room" className="h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid="karaoke-staff-room" />
      </div>
      {songs.map((s, i) => (
        <div key={i} className="flex flex-wrap gap-2">
          <input value={s.url} onChange={(e) => setSongs(songs.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))} placeholder={`Song ${i + 1} — YouTube link`} className={`flex-1 min-w-[180px] h-10 rounded-xl border px-3 text-sm ${s.url && !parseYouTubeId(s.url) ? 'border-rose-400' : 'border-slate-200'}`} data-testid={`karaoke-staff-url-${i + 1}`} />
          <input value={s.title} onChange={(e) => setSongs(songs.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} placeholder="Title (optional)" className="flex-1 min-w-[140px] h-10 rounded-xl border border-slate-200 px-3 text-sm" />
          {songs.length > 1 && <button type="button" onClick={() => setSongs(songs.filter((_, j) => j !== i))} className="h-10 w-10 rounded-xl bg-slate-100 inline-flex items-center justify-center" aria-label="Remove song"><X className="w-4 h-4" /></button>}
        </div>
      ))}
      <div className="flex gap-2">
        {songs.length < 3 && <button type="button" onClick={() => setSongs([...songs, { url: '', title: '' }])} className="h-10 px-3 rounded-xl bg-slate-100 text-sm font-semibold inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Song</button>}
        <button type="button" disabled={!valid || busy} onClick={submit} className="h-10 px-4 rounded-xl bg-fuchsia-600 text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="karaoke-staff-submit"><Send className="w-4 h-4" /> Send request</button>
      </div>
    </div>
  );
};
